import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link, useParams, Routes, Route } from "react-router-dom";
import {
  Menu, CheckCircle2, Circle, CircleDot, ChevronRight, ChevronDown,
  Save, FileDown, RotateCcw, CheckCircle, Award, X, Home, FolderOpen,
} from "lucide-react";
import { TEMPLATES, TEMPLATE_MAP, getTemplatesByModule } from "./templates-index";
import { MODULES, MODULE_MAP } from "./modules";
import type { TemplateData, RepeatableRow, OfficerProfile, LepcState } from "./types";
import {
  loadState, saveState, saveOfficer, getOfficer, isOnboarded,
  getCompletionStats, getModuleCompletion, getDaysRemaining,
  resetTemplateData, markTemplateComplete, clearAllData,
} from "./storage";
import { TemplateRenderer, ContextBox } from "./FieldRenderer";
import { exportTemplatePDF, exportPortfolioPDF } from "./pdf-export";
import "./lepc.css";

// ── Toast ──

function Toast({ message, show }: { message: string; show: boolean }) {
  if (!show) return null;
  return (
    <div className="lepc-toast">
      <Save size={14} />
      {message}
    </div>
  );
}

// ── Onboarding ──

function Onboarding({ onComplete }: { onComplete: (officer: OfficerProfile) => void }) {
  const [form, setForm] = useState<OfficerProfile>({
    fullName: "",
    badgeNumber: "",
    department: "",
    patrolDistrict: "",
    rank: "",
    enrollmentDate: "",
    targetCompletionDate: "",
  });

  const handleEnrollDate = (date: string) => {
    let target = "";
    if (date) {
      const d = new Date(date);
      d.setFullYear(d.getFullYear() + 1);
      target = d.toISOString().split("T")[0];
    }
    setForm({ ...form, enrollmentDate: date, targetCompletionDate: target });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName || !form.badgeNumber || !form.department) return;
    onComplete(form);
  };

  return (
    <div className="lepc-app">
      <div className="lepc-header">
        <div className="lepc-header-brand">
          <img src="/sentinel_logo.png" alt="Sentinel" className="lepc-header-logo" />
          <div className="lepc-header-titles">
            <div className="lepc-header-wordmark">Sentinel</div>
            <div className="lepc-header-subtitle">LEP-C Professional Template System</div>
          </div>
        </div>
      </div>

      <div className="lepc-onboarding">
        <div className="lepc-onboarding-card">
          <div className="lepc-onboarding-title">Welcome to the LEP-C Program</div>
          <div className="lepc-onboarding-subtitle">
            Program S-LELSC, Level 1 — Law Enforcement Professional Certified (LEP-C).
            Enter your details to initialize your 24 Master Output templates.
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lepc-field">
              <label className="lepc-field-label">Officer Full Name *</label>
              <input
                className="lepc-input"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="lepc-onboarding-form-row">
              <div className="lepc-field">
                <label className="lepc-field-label">Badge Number or Employee ID *</label>
                <input
                  className="lepc-input"
                  value={form.badgeNumber}
                  onChange={(e) => setForm({ ...form, badgeNumber: e.target.value })}
                  placeholder="e.g., Badge #4471"
                  required
                />
              </div>
              <div className="lepc-field">
                <label className="lepc-field-label">Department/Agency Name *</label>
                <input
                  className="lepc-input"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="e.g., Metro Police Department"
                  required
                />
              </div>
            </div>

            <div className="lepc-onboarding-form-row">
              <div className="lepc-field">
                <label className="lepc-field-label">Assigned Patrol District</label>
                <input
                  className="lepc-input"
                  value={form.patrolDistrict}
                  onChange={(e) => setForm({ ...form, patrolDistrict: e.target.value })}
                  placeholder="e.g., District 4 - North Patrol"
                />
              </div>
              <div className="lepc-field">
                <label className="lepc-field-label">Current Rank/Title</label>
                <input
                  className="lepc-input"
                  value={form.rank}
                  onChange={(e) => setForm({ ...form, rank: e.target.value })}
                  placeholder="e.g., Police Officer"
                />
              </div>
            </div>

            <div className="lepc-onboarding-form-row">
              <div className="lepc-field">
                <label className="lepc-field-label">Enrollment Date *</label>
                <input
                  type="date"
                  className="lepc-input"
                  value={form.enrollmentDate}
                  onChange={(e) => handleEnrollDate(e.target.value)}
                  required
                />
              </div>
              <div className="lepc-field">
                <label className="lepc-field-label">Target Completion Date (auto-calculated)</label>
                <input
                  type="date"
                  className="lepc-input"
                  value={form.targetCompletionDate}
                  readOnly
                  style={{ background: "#f5f5f5" }}
                />
              </div>
            </div>

            <button type="submit" className="lepc-btn lepc-btn-primary" style={{ width: "100%", marginTop: "0.5rem", justifyContent: "center" }}>
              Begin My LEP-C Program
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ──

function Sidebar({ open, onClose, activeTemplateId }: { open: boolean; onClose: () => void; activeTemplateId?: string }) {
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set([1, 2, 3, 4, 5, 6]));
  const [version, setVersion] = useState(0);
  // Force re-render when data changes
  useEffect(() => {
    const interval = setInterval(() => setVersion((v) => v + 1), 2000);
    return () => clearInterval(interval);
  }, []);

  const toggleModule = (id: number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getStatusIcon = (templateId: string) => {
    const state = loadState();
    const data = state.templates[templateId];
    if (!data || data.status === "not_started") {
      return <Circle size={14} color="#9ca3af" />;
    }
    if (data.status === "complete") {
      return <CheckCircle2 size={14} color="#2D7D46" />;
    }
    return <CircleDot size={14} color="#CC7A00" />;
  };

  return (
    <>
      <div className={`lepc-sidebar ${open ? "open" : ""}`}>
        <Link to="/lepc" className="lepc-sidebar-module-header" onClick={onClose} style={{ borderBottom: "1px solid var(--lepc-border)", marginBottom: "0.5rem" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Home size={14} />
            Dashboard
          </span>
        </Link>
        {MODULES.map((mod) => {
          const completion = getModuleCompletion(mod.id);
          return (
            <div key={mod.id} className="lepc-sidebar-section">
              <div className="lepc-sidebar-module-header" onClick={() => toggleModule(mod.id)}>
                <span style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                  <ChevronRight size={12} className={`chevron ${expandedModules.has(mod.id) ? "open" : ""}`} />
                  Module {mod.id}
                </span>
                <span style={{ fontSize: "0.6875rem", color: "#9ca3af" }}>
                  {completion.complete}/{completion.total}
                </span>
              </div>
              <div className="lepc-sidebar-module-stage">{mod.stage}</div>
              {expandedModules.has(mod.id) && (
                <div className="lepc-sidebar-templates">
                  {getTemplatesByModule(mod.id).map((tpl) => (
                    <Link
                      key={tpl.id}
                      to={`/lepc/template/${tpl.id}`}
                      onClick={onClose}
                      className={`lepc-sidebar-template ${activeTemplateId === tpl.id ? "active" : ""}`}
                    >
                      <span className="lepc-sidebar-status-icon">{getStatusIcon(tpl.id)}</span>
                      <span>{tpl.number}. {tpl.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <Link to="/lepc/portfolio" className="lepc-sidebar-template" onClick={onClose} style={{ marginTop: "0.5rem", fontWeight: 600 }}>
          <FolderOpen size={14} color="#1B3A6B" />
          Professional Portfolio
        </Link>
      </div>
      {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 98, background: "rgba(0,0,0,0.3)" }} />}
    </>
  );
}

// ── Dashboard ──

function Dashboard({ onNavigate }: { onNavigate: (path: string) => void }) {
  const officer = getOfficer();
  const stats = getCompletionStats();
  const daysRemaining = getDaysRemaining();
  const overallPct = Math.round((stats.complete / stats.total) * 100);

  return (
    <div className="lepc-main">
      {/* Architecture Banner */}
      <div className="lepc-banner">
        <div className="lepc-banner-title">S-LELSC | Level 1 — Associate | LEP-C</div>
        <div className="lepc-banner-subtitle">Upgrade Path: LEP-C → LES-C → LEM-C → SLEE-C</div>
      </div>

      {/* Overall progress + days remaining */}
      <div className="lepc-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <div className="lepc-card-title">Program Progress</div>
          <div style={{ fontSize: "0.875rem", color: "#555" }}>
            {daysRemaining !== null && `${daysRemaining} days remaining`}
          </div>
        </div>
        <div className="lepc-progress-bar">
          <div className={`lepc-progress-fill ${overallPct === 100 ? "complete" : ""}`} style={{ width: `${overallPct}%` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", fontSize: "0.8125rem", color: "#555" }}>
          <span>{stats.complete} of {stats.total} templates complete</span>
          <span>{overallPct}%</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
          <button className="lepc-btn lepc-btn-gold" onClick={() => exportAllCompleted()}>
            <FileDown size={14} />
            Export All Completed Templates
          </button>
          <button className="lepc-btn lepc-btn-outline" onClick={() => onNavigate("/lepc/portfolio")}>
            <FolderOpen size={14} />
            View Professional Portfolio
          </button>
        </div>
      </div>

      {/* Module cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
        {MODULES.map((mod) => {
          const completion = getModuleCompletion(mod.id);
          const pct = Math.round((completion.complete / completion.total) * 100);
          return (
            <div
              key={mod.id}
              className="lepc-module-card"
              onClick={() => onNavigate(`/lepc/template/${mod.templateIds[0]}`)}
            >
              <div className="lepc-module-card-name">Module {mod.id}: {mod.name}</div>
              <div className="lepc-module-card-stage">{mod.stage}</div>
              <div className="lepc-progress-bar">
                <div className={`lepc-progress-fill ${pct === 100 ? "complete" : ""}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="lepc-module-card-progress-text">
                <span>{completion.complete} of {completion.total} templates complete</span>
                <span>{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function exportAllCompleted() {
  const state = loadState();
  for (const tpl of TEMPLATES) {
    const data = state.templates[tpl.id];
    if (data && data.status === "complete") {
      exportTemplatePDF(tpl, data);
    }
  }
}

// ── Template Page ──

function TemplatePage({ templateId, onNavigate }: { templateId: string; onNavigate: (path: string) => void }) {
  const template = TEMPLATE_MAP[templateId];
  const [data, setData] = useState<TemplateData | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showCelebration, setShowCelebration] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (template) {
      const state = loadState();
      setData(state.templates[templateId] ?? {
        fields: {}, repeatables: {}, checklists: {}, checklistNotes: {}, status: "not_started", lastSaved: "",
      });
    }
  }, [templateId, template]);

  // Auto-save
  useEffect(() => {
    if (!data) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveState({ ...loadState(), templates: { ...loadState().templates, [templateId]: data } });
      setShowToast(true);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setShowToast(false), 2000);
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [data, templateId]);

  // Periodic auto-save (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (data) {
        saveState({ ...loadState(), templates: { ...loadState().templates, [templateId]: data } });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [data, templateId]);

  if (!template || !data) {
    return <div className="lepc-main"><p>Template not found.</p></div>;
  }

  const mod = MODULE_MAP[template.module];
  const officer = getOfficer();

  const handleMarkComplete = () => {
    const updated = { ...data, status: "complete" as const, lastSaved: new Date().toISOString() };
    setData(updated);
    saveState({ ...loadState(), templates: { ...loadState().templates, [templateId]: updated } });

    // Check if module is now complete
    const completion = getModuleCompletion(template.module);
    if (completion.complete === completion.total) {
      setShowCelebration("module");
    }

    // Check if all 24 are complete
    const stats = getCompletionStats();
    if (stats.complete === stats.total) {
      setShowCelebration("program");
    }
  };

  const handleReset = () => {
    resetTemplateData(templateId);
    setData({
      fields: {}, repeatables: {}, checklists: {}, checklistNotes: {}, status: "not_started", lastSaved: "",
    });
    setShowResetConfirm(false);
  };

  const isComplete = data.status === "complete";

  return (
    <div className="lepc-main">
      {/* Breadcrumb */}
      <div className="lepc-breadcrumb">
        <Link to="/lepc">Sentinel</Link>
        <span className="lepc-breadcrumb-sep">›</span>
        <span>S-LELSC L1</span>
        <span className="lepc-breadcrumb-sep">›</span>
        <span>Module {template.module}: {mod?.name}</span>
        <span className="lepc-breadcrumb-sep">›</span>
        <span>{template.name}</span>
      </div>

      {/* Template header */}
      <div className="lepc-template-header">
        <div className="lepc-template-title-row">
          <h1 className="lepc-template-name">{template.name}</h1>
          <span className="lepc-credential-badge">LEP-C</span>
          <span className="lepc-stage-pill">{template.stage}</span>
        </div>
        <div className="lepc-template-module">Module {template.module}: {mod?.name}</div>
      </div>

      {/* Architecture context */}
      <ContextBox text={template.architectureContext} />

      {/* Why this tool exists */}
      <div className="lepc-why-section">
        <div className="lepc-why-label">Why This Tool Exists</div>
        <div className="lepc-why-text">{template.whyExists}</div>
      </div>

      {/* Template form */}
      <TemplateRenderer
        template={template}
        data={data}
        onDataChange={setData}
      />

      {/* Action bar */}
      <div className="lepc-action-bar">
        <div className="lepc-action-bar-left">
          {showToast && <span style={{ color: "#2D7D46", display: "flex", alignItems: "center", gap: "0.25rem" }}>
            <Save size={12} /> Saved
          </span>}
          {isComplete && <span style={{ color: "#2D7D46", display: "flex", alignItems: "center", gap: "0.25rem" }}>
            <CheckCircle size={12} /> Marked Complete
          </span>}
        </div>
        <div className="lepc-action-bar-right">
          <button className="lepc-btn lepc-btn-outline" onClick={() => {
            saveState({ ...loadState(), templates: { ...loadState().templates, [templateId]: data } });
            setShowToast(true);
            setTimeout(() => setShowToast(false), 2000);
          }}>
            <Save size={14} /> Save Progress
          </button>
          <button
            className={isComplete ? "lepc-btn lepc-btn-outline" : "lepc-btn lepc-btn-success"}
            onClick={handleMarkComplete}
          >
            <CheckCircle size={14} /> {isComplete ? "Completed" : "Mark as Complete"}
          </button>
          <button className="lepc-btn lepc-btn-gold" onClick={() => exportTemplatePDF(template, data)}>
            <FileDown size={14} /> Export to PDF
          </button>
          <button className="lepc-btn lepc-btn-danger" onClick={() => setShowResetConfirm(true)}>
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      {/* Reset confirmation */}
      {showResetConfirm && (
        <div className="lepc-celebration-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="lepc-celebration-card" onClick={(e) => e.stopPropagation()}>
            <div className="lepc-celebration-icon" style={{ background: "#CC0000" }}>
              <RotateCcw size={28} />
            </div>
            <div className="lepc-celebration-title">Reset This Template?</div>
            <div className="lepc-celebration-text">
              This will permanently delete all data you have entered in this template. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button className="lepc-btn lepc-btn-outline" onClick={() => setShowResetConfirm(false)}>
                Cancel
              </button>
              <button className="lepc-btn lepc-btn-danger" onClick={handleReset}>
                Yes, Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Celebration overlay */}
      {showCelebration && (
        <div className="lepc-celebration-overlay" onClick={() => setShowCelebration(null)}>
          <div className="lepc-celebration-card" onClick={(e) => e.stopPropagation()}>
            <div className="lepc-celebration-icon">
              <Award size={28} />
            </div>
            {showCelebration === "module" && (
              <>
                <div className="lepc-celebration-title">Module {template.module} Complete!</div>
                <div className="lepc-celebration-text">
                  You have completed all 4 templates in Module {template.module}: {mod?.name}.
                  <br />
                  <strong>{mod?.stage}</strong>
                  <br /><br />
                  Advance to Module {template.module + 1 <= 6 ? template.module + 1 : template.module} to continue your LEP-C journey.
                </div>
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                  {template.module < 6 && (
                    <button
                      className="lepc-btn lepc-btn-primary"
                      onClick={() => {
                        setShowCelebration(null);
                        const nextMod = MODULES.find((m) => m.id === template.module + 1);
                        if (nextMod) onNavigate(`/lepc/template/${nextMod.templateIds[0]}`);
                      }}
                    >
                      Advance to Module {template.module + 1}
                    </button>
                  )}
                  <button className="lepc-btn lepc-btn-outline" onClick={() => setShowCelebration(null)}>
                    Back to Dashboard
                  </button>
                </div>
              </>
            )}
            {showCelebration === "program" && (
              <>
                <div className="lepc-celebration-title">LEP-C Program Complete!</div>
                <div className="lepc-celebration-text">
                  Congratulations, {officer?.fullName ?? "Officer"}!
                  <br /><br />
                  You have completed all 24 Master Output templates for the Sentinel LEP-C certification.
                  <br /><br />
                  Completion Date: {new Date().toLocaleDateString()}
                  <br /><br />
                  Your <strong>Final Certification Exam</strong> is now available.
                </div>
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                  <button className="lepc-btn lepc-btn-gold" onClick={() => exportPortfolioPDF()}>
                    <FileDown size={14} /> Export Portfolio
                  </button>
                  <button className="lepc-btn lepc-btn-outline" onClick={() => {
                    setShowCelebration(null);
                    onNavigate("/lepc");
                  }}>
                    Back to Dashboard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Toast message="Saved" show={showToast} />
    </div>
  );
}

// ── Portfolio Page ──

function PortfolioPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const state = loadState();
  const stats = getCompletionStats();

  return (
    <div className="lepc-main">
      <div className="lepc-breadcrumb">
        <Link to="/lepc">Sentinel</Link>
        <span className="lepc-breadcrumb-sep">›</span>
        <span>S-LELSC L1</span>
        <span className="lepc-breadcrumb-sep">›</span>
        <span>Professional Portfolio</span>
      </div>

      <div className="lepc-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div className="lepc-card-title">Professional Portfolio Summary</div>
          <button className="lepc-btn lepc-btn-gold" onClick={() => exportPortfolioPDF()}>
            <FileDown size={14} /> Export Portfolio PDF
          </button>
        </div>
        <div className="lepc-progress-bar" style={{ marginBottom: "0.5rem" }}>
          <div className={`lepc-progress-fill ${stats.complete === stats.total ? "complete" : ""}`} style={{ width: `${(stats.complete / stats.total) * 100}%` }} />
        </div>
        <div style={{ fontSize: "0.875rem", color: "#555" }}>
          {stats.complete} of {stats.total} templates complete | {stats.inProgress} in progress | {stats.notStarted} not started
        </div>
      </div>

      {MODULES.map((mod) => {
        const templates = getTemplatesByModule(mod.id);
        return (
          <div key={mod.id} className="lepc-card">
            <div className="lepc-card-title">Module {mod.id}: {mod.name}</div>
            <div className="lepc-card-description">{mod.stage} | {mod.pillars}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {templates.map((tpl) => {
                const data = state.templates[tpl.id];
                const status = data?.status ?? "not_started";
                const statusIcon = status === "complete" ? <CheckCircle2 size={16} color="#2D7D46" /> : status === "in_progress" ? <CircleDot size={16} color="#CC7A00" /> : <Circle size={16} color="#9ca3af" />;
                return (
                  <div
                    key={tpl.id}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.75rem",
                      padding: "0.75rem", borderRadius: "6px",
                      border: "1px solid #E5E7EB", cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    onClick={() => onNavigate(`/lepc/template/${tpl.id}`)}
                  >
                    {statusIcon}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1B3A6B" }}>
                        Template {tpl.number}: {tpl.name}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        {status === "complete" ? "Complete" : status === "in_progress" ? "In Progress" : "Not Started"}
                      </div>
                    </div>
                    <ChevronRight size={16} color="#9ca3af" />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main LEP-C App ──

export function LepcApp() {
  const [onboarded, setOnboarded] = useState(isOnboarded());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const handleOnboard = (officer: OfficerProfile) => {
    saveOfficer(officer);
    setOnboarded(true);
    navigate("/lepc");
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  if (!onboarded) {
    return <Onboarding onComplete={handleOnboard} />;
  }

  const officer = getOfficer();
  const templateId = params.templateId;

  const handleNameSave = () => {
    if (officer && nameInput.trim()) {
      const updated = { ...officer, fullName: nameInput.trim() };
      saveOfficer(updated);
      setEditName(false);
    }
  };

  return (
    <div className="lepc-app">
      {/* Header */}
      <div className="lepc-header">
        <div className="lepc-header-brand">
          <button className="lepc-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={20} />
          </button>
          <img src="/sentinel_logo.png" alt="Sentinel" className="lepc-header-logo" />
          <div className="lepc-header-titles">
            <div className="lepc-header-wordmark">Sentinel</div>
            <div className="lepc-header-subtitle">LEP-C Professional Template System</div>
          </div>
        </div>
        <div className="lepc-header-user">
          {editName ? (
            <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
              <input
                className="lepc-input"
                style={{ width: "150px", padding: "0.25rem 0.5rem", fontSize: "0.8125rem" }}
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                autoFocus
              />
              <button className="lepc-btn lepc-btn-primary" style={{ padding: "0.25rem 0.625rem", fontSize: "0.75rem" }} onClick={handleNameSave}>
                Save
              </button>
              <button className="lepc-btn lepc-btn-outline" style={{ padding: "0.25rem 0.625rem", fontSize: "0.75rem" }} onClick={() => setEditName(false)}>
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="lepc-header-user-name" onClick={() => { setNameInput(officer?.fullName ?? ""); setEditName(true); }}>
              {officer?.fullName ?? "Officer"}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="lepc-body">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} activeTemplateId={templateId} />

        <Routes>
          <Route path="/" element={<Dashboard onNavigate={handleNavigate} />} />
          <Route path="/template/:templateId" element={<TemplatePageWrapper onNavigate={handleNavigate} />} />
          <Route path="/portfolio" element={<PortfolioPage onNavigate={handleNavigate} />} />
        </Routes>
      </div>
    </div>
  );
}

// ── Wrapper to get templateId from params ──

function TemplatePageWrapper({ onNavigate }: { onNavigate: (path: string) => void }) {
  const params = useParams();
  return <TemplatePage templateId={params.templateId!} onNavigate={onNavigate} />;
}
